/**
 * KinsCircle Phase 6A + 6B — AI Diagnostics Card
 *
 * Owner / admin only. Hidden by default behind the ENABLE_AI_DIAGNOSTICS flag.
 * Pure additive UI; never mutates anything. Reads the local AI usage log
 * recorded by `aiMemoryService.ts` plus the Phase 6B layers:
 *   - rollout mode + active flags (featureFlag)
 *   - per-plan quota counters (aiQuota)
 *   - cost/failure-rate/spike warnings (aiCostSafety)
 *   - quality feedback summary (aiFeedback)
 *
 * Renders null when:
 *   - the diagnostics flag is OFF, OR
 *   - the viewer is not owner/admin
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sparkles,
  RefreshCw,
  Trash2,
  TrendingUp,
  AlertCircle,
  ShieldAlert,
  DollarSign,
  Activity,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';

import {
  canRunAIDiagnostics,
  getAIRolloutMode,
  isAIRolloutControlEnabled,
  isAIUsageLimitsEnabled,
  isAIQualityFeedbackEnabled,
  isAISafetyFilterEnabled,
  isAIMemoryWriterEnabled,
  isAITimelineEnabled,
  isAIOnThisDayEnabled,
  isAIMemoryTagsEnabled,
} from '@/lib/ai/featureFlag';
import {
  readAIUsageLog,
  clearAIUsageLog,
  type AIUsageEntry,
} from '@/lib/ai/aiMemoryService';
import {
  readAIQuotaSummary,
  resetAIQuotaCounters,
} from '@/lib/ai/aiQuota';
import { computeAICostStats } from '@/lib/ai/aiCostSafety';
import { summarizeAIFeedback } from '@/lib/ai/aiFeedback';
import { useAuth } from '@/contexts/AuthContext';

const fmtTime = (ts: number): string =>
  new Date(ts).toLocaleString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
  });

const AIDiagnosticsCard: React.FC = () => {
  const { isOwner, isAdmin, role: authRole, plan: authPlan } = useAuth();
  const role = isOwner ? 'owner' : isAdmin ? 'admin' : authRole ?? 'member';
  const plan = authPlan ?? 'free';
  const visible = canRunAIDiagnostics(role);

  const [log, setLog] = useState<AIUsageEntry[]>([]);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    setLog(readAIUsageLog());
  }, []);

  useEffect(() => {
    if (!visible) return;
    refresh();
  }, [visible, refresh, tick]);

  const stats = useMemo(() => {
    const total = log.length;
    const ok = log.filter((e) => e.ok).length;
    const failed = total - ok;
    const tokens = log.reduce((sum, e) => sum + (e.tokensUsed ?? 0), 0);
    const avgMs =
      total === 0
        ? 0
        : Math.round(log.reduce((sum, e) => sum + e.durationMs, 0) / total);
    const successRate = total === 0 ? 0 : Math.round((ok / total) * 100);
    const byAction: Record<string, number> = {};
    log.forEach((e) => {
      byAction[e.action] = (byAction[e.action] ?? 0) + 1;
    });
    return { total, ok, failed, tokens, avgMs, successRate, byAction };
  }, [log]);

  const recentFailures = useMemo(
    () => log.filter((e) => !e.ok).slice(0, 5),
    [log],
  );

  // Phase 6B aggregate views
  const cost = useMemo(() => computeAICostStats(), [log, tick]);
  const quota = useMemo(() => readAIQuotaSummary(role, plan), [role, plan, tick]);
  const feedback = useMemo(() => summarizeAIFeedback(), [tick]);

  if (!visible) return null;

  const rolloutMode = getAIRolloutMode();
  const flagBadges: { label: string; on: boolean }[] = [
    { label: 'memoryWriter', on: isAIMemoryWriterEnabled() },
    { label: 'timeline', on: isAITimelineEnabled() },
    { label: 'onThisDay', on: isAIOnThisDayEnabled() },
    { label: 'memoryTags', on: isAIMemoryTagsEnabled() },
    { label: 'rolloutControl', on: isAIRolloutControlEnabled() },
    { label: 'usageLimits', on: isAIUsageLimitsEnabled() },
    { label: 'qualityFeedback', on: isAIQualityFeedbackEnabled() },
    { label: 'safetyFilter', on: isAISafetyFilterEnabled() },
  ];

  return (
    <Card className="border-fuchsia-100">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-600" />
              AI usage diagnostics
            </CardTitle>
            <CardDescription>
              Read-only summary of AI memory calls from this browser. Never
              writes to the database; never modifies any memory.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full gap-1.5"
              onClick={() => setTick((t) => t + 1)}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full gap-1.5 text-slate-500"
              onClick={() => {
                clearAIUsageLog();
                resetAIQuotaCounters();
                setTick((t) => t + 1);
              }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear log
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* ── Rollout + flag status ──────────────────────────────────── */}
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Rollout mode
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${
                rolloutMode === 'disabled'
                  ? 'bg-slate-50 text-slate-600 border-slate-200'
                  : 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200'
              }`}
            >
              {rolloutMode}
            </span>
            {!isAIRolloutControlEnabled() && (
              <span className="text-[10px] text-slate-500 italic">
                (rollout-control flag is off — legacy gating active)
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {flagBadges.map((f) => (
              <span
                key={f.label}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${
                  f.on
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-50 text-slate-500 border-slate-200'
                }`}
              >
                {f.label}
                <span className="opacity-70">{f.on ? 'on' : 'off'}</span>
              </span>
            ))}
          </div>
        </div>

        {/* ── Cost + failure warnings ────────────────────────────────── */}
        {cost.warnings.length > 0 && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 space-y-1.5">
            <div className="text-xs font-semibold text-rose-800 uppercase tracking-wide flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" />
              Cost / health warnings
            </div>
            {cost.warnings.map((w, i) => (
              <div key={i} className="text-xs text-rose-900 flex items-start gap-1.5">
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                    w.level === 'critical'
                      ? 'bg-rose-600 text-white'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {w.level}
                </span>
                <span>{w.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Headline tiles ─────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Tile label="Calls" value={stats.total} />
          <Tile label="Success" value={`${stats.successRate}%`} accent="emerald" />
          <Tile
            label="Failures"
            value={stats.failed}
            accent={stats.failed > 0 ? 'rose' : 'slate'}
          />
          <Tile label="Avg ms" value={stats.avgMs} />
          <Tile label="Tokens" value={stats.tokens} />
        </div>

        {/* ── Cost estimates ─────────────────────────────────────────── */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" />
            Estimated cost (advisory)
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile label="Today calls" value={cost.totalCallsToday} />
            <Tile label="Month calls" value={cost.totalCallsMonth} />
            <Tile label="Today $" value={`$${cost.estDailyCostUsd.toFixed(3)}`} />
            <Tile label="Month $" value={`$${cost.estMonthlyCostUsd.toFixed(2)}`} />
          </div>
          <div className="mt-2 text-[10px] text-slate-500">
            Estimates only. Real cost is determined by the model, prompt size,
            and provider. Threshold: $1.00/day, $20.00/month.
          </div>
        </div>

        {/* ── Quota usage ────────────────────────────────────────────── */}
        {isAIUsageLimitsEnabled() && (
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" />
              Quota — plan: {quota.plan}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <QuotaBlock title="Story bucket" cur={quota.story} />
              <QuotaBlock title="Lite bucket" cur={quota.lite} />
            </div>
          </div>
        )}

        {Object.keys(stats.byAction).length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              Calls by action
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.byAction)
                .sort((a, b) => b[1] - a[1])
                .map(([action, count]) => (
                  <span
                    key={action}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-xs"
                  >
                    <span className="font-medium text-slate-700">{action}</span>
                    <span className="text-slate-500">{count}</span>
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* ── Feedback summary ───────────────────────────────────────── */}
        {isAIQualityFeedbackEnabled() && feedback.total > 0 && (
          <div className="rounded-lg border border-fuchsia-200 bg-fuchsia-50/50 p-3">
            <div className="text-xs font-semibold text-fuchsia-800 uppercase tracking-wide mb-2">
              Feedback ({feedback.total} response{feedback.total === 1 ? '' : 's'})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(Object.entries(feedback.byTag) as [string, number][])
                .filter(([, count]) => count > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([tag, count]) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-fuchsia-200 text-[11px]"
                  >
                    <span className="font-medium text-slate-700">{tag.replace('_', ' ')}</span>
                    <span className="text-slate-500">{count}</span>
                  </span>
                ))}
            </div>
          </div>
        )}

        {recentFailures.length > 0 && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
            <div className="text-xs font-semibold text-rose-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              Recent failures
            </div>
            <ul className="space-y-1.5">
              {recentFailures.map((e) => (
                <li key={e.id} className="text-xs text-rose-900 flex flex-wrap gap-2">
                  <span className="text-rose-600">{fmtTime(e.ts)}</span>
                  <span className="font-medium">{e.action}</span>
                  <span className="text-rose-700">— {e.code ?? 'unknown'}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {log.length === 0 && (
          <div className="text-sm text-slate-500 italic">
            No AI calls yet on this device.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Tile: React.FC<{
  label: string;
  value: number | string;
  accent?: 'emerald' | 'rose' | 'slate';
}> = ({ label, value, accent }) => {
  const cls =
    accent === 'emerald'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
      : accent === 'rose'
      ? 'border-rose-200 bg-rose-50 text-rose-800'
      : 'border-slate-200 bg-white text-slate-800';
  return (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
};

const QuotaBlock: React.FC<{
  title: string;
  cur: { day: number; week: number; month: number; cap: { day: number; week: number; month: number } };
}> = ({ title, cur }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-2.5">
    <div className="text-[11px] font-semibold text-slate-700 mb-1.5">{title}</div>
    <div className="space-y-1">
      <Bar label="Day" used={cur.day} cap={cur.cap.day} />
      <Bar label="Week" used={cur.week} cap={cur.cap.week} />
      <Bar label="Month" used={cur.month} cap={cur.cap.month} />
    </div>
  </div>
);

const Bar: React.FC<{ label: string; used: number; cap: number }> = ({ label, used, cap }) => {
  const pct = cap === 0 ? 100 : Math.min(100, Math.round((used / cap) * 100));
  const tone = pct >= 100 ? 'bg-rose-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="text-[10px]">
      <div className="flex items-center justify-between text-slate-600">
        <span>{label}</span>
        <span className="tabular-nums">
          {used}
          <span className="text-slate-400"> / {cap || '—'}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default AIDiagnosticsCard;
